/**
 * El foco, para quien no usa el mouse.
 *
 * Un banco público tiene obligaciones de accesibilidad, y un diálogo que no atrapa el foco las
 * incumple de la peor manera: con Tab el teclado se escapa al fondo de la pantalla, donde el lector
 * sigue leyendo una cartera que el usuario ya no ve. Y al cerrar, si el foco no vuelve al botón que
 * abrió el diálogo, vuelve al principio del documento: cada acción cuesta veinte pulsaciones.
 *
 * Son dos comportamientos distintos y se usan por separado:
 *
 * - `useFocusTrap` para lo modal — un diálogo tapa la pantalla, y mientras esté abierto es la
 *   pantalla entera.
 * - `useFocusReturn` para lo que convive con el fondo — el cajón de evidencia se lee **al lado** del
 *   informe, así que atrapar el foco ahí impediría exactamente lo que el cajón existe para hacer.
 *
 * Nada de esto tiene prueba unitaria: `vitest` corre sin DOM en este proyecto, y montar jsdom para
 * cubrir treinta líneas de `addEventListener` costaría más de lo que atrapa. Se verifica con el
 * teclado, que es como lo va a usar la persona.
 */

import { useEffect, type RefObject } from "react";

const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

function focusable(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => el.offsetParent !== null || el === document.activeElement,
  );
}

/** Mientras el diálogo esté abierto, Tab no sale de él; al cerrarse, el foco vuelve de donde vino. */
export function useFocusTrap(ref: RefObject<HTMLElement | null>, active = true): void {
  useEffect(() => {
    if (!active) return;
    const root = ref.current;
    if (!root) return;
    const returnTo = document.activeElement as HTMLElement | null;

    const first = focusable(root)[0] ?? root;
    first.focus();

    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== "Tab" || !root) return;
      const items = focusable(root);
      if (items.length === 0) {
        event.preventDefault();
        return;
      }
      const current = document.activeElement as HTMLElement | null;
      const index = current ? items.indexOf(current) : -1;
      const next = moveFocus(items.length, index, event.shiftKey);
      event.preventDefault();
      items[next]!.focus();
    }

    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      returnTo?.focus?.();
    };
  }, [ref, active]);
}

/** El foco entra al panel al abrirse y vuelve al control que lo abrió, sin quedar atrapado. */
export function useFocusReturn(ref: RefObject<HTMLElement | null>, active = true): void {
  useEffect(() => {
    if (!active) return;
    const returnTo = document.activeElement as HTMLElement | null;
    const root = ref.current;
    (focusable(root ?? document.body)[0] ?? root)?.focus?.();
    return () => returnTo?.focus?.();
  }, [ref, active]);
}

/**
 * El índice siguiente al recorrer con teclado, dando la vuelta en los extremos.
 *
 * Vive acá y no dentro del `useEffect` porque es la única parte con una decisión adentro — el
 * ciclo — y es la única que se puede probar sin un navegador. La usan el atrapado de foco y las
 * flechas de la cola, para que las dos den la vuelta igual.
 */
export function moveFocus(count: number, current: number, backwards: boolean): number {
  if (count <= 0) return 0;
  if (current < 0) return backwards ? count - 1 : 0;
  return (current + (backwards ? -1 : 1) + count) % count;
}
